export type PlanId = 'free' | 'starter' | 'pro' | 'custom';

export interface PlanQuotas {
  messages_per_month: number;
  kb_chars_per_workspace: number;
  workspaces_max: number;
  users_managers_max: number;
  users_viewers_max: number;
  callbot_minutes_per_month: number;
}

export interface PlanFeatures {
  advanced_customization: boolean;
  conversations_access: boolean;
  multi_workspace_ui: boolean;
  branding_required: boolean;
}

export const DEFAULT_PLAN_POSITIONS: Record<PlanId, number> = {
  free: 0,
  starter: 1,
  pro: 2,
  custom: 3,
};

export interface PlanConfig {
  id: string;
  productId?: string | null;
  plan_id: PlanId;
  position?: number;
  display_name: string;
  name: string;
  plan: string;
  type: string;
  amount: number;
  unit_amount?: number;
  currency?: string;
  interval?: 'month' | 'year' | null;
  expose_in_ui?: boolean;
  short_description?: string | null;
  long_description?: string | null;
  varaiblesUsage?: string | null;
  up_to?: number;
  trialPeriodDays?: number;
  quotas: PlanQuotas;
  channels_available: string[];
  features: PlanFeatures;
  support_level?: string | null;
}

export interface AccountUsage {
  messages_count: number;
  callbot_seconds_count: number;
  workspaces_count?: number;
  users_managers_count?: number;
  viewers_count?: number;
  last_reset_at?: unknown;
}

export interface AccountPlan {
  plan_id: PlanId;
  usage: AccountUsage;
  plan: PlanConfig;
  custom_overrides?: Record<string, unknown> | null;
  manual_billing?: {
    payment_type?: AdminPaymentType;
    payment_method?: ManualPaymentMethod;
    notes?: string | null;
    endday?: number;
    duration_days?: number;
  } | null;
  billingSubscription?: Record<string, unknown> | null;
}

export type AdminPaymentType = 'manual' | 'custom';

export type ManualPaymentMethod =
  | 'wire_transfer'
  | 'bank_transfer'
  | 'invoice'
  | 'check'
  | 'paypal'
  | 'cash'
  | 'other';

export const MANUAL_PAYMENT_METHODS: Array<{
  id: ManualPaymentMethod;
  label: string;
  icon: string;
}> = [
  { id: 'wire_transfer', label: 'Wire transfer', icon: 'account_balance' },
  { id: 'bank_transfer', label: 'Bank transfer', icon: 'savings' },
  { id: 'invoice', label: 'Invoice', icon: 'receipt_long' },
  { id: 'check', label: 'Check', icon: 'payments' },
  { id: 'paypal', label: 'PayPal', icon: 'account_balance_wallet' },
  { id: 'cash', label: 'Cash', icon: 'attach_money' },
  { id: 'other', label: 'Other', icon: 'more_horiz' },
];

export interface AssignUserPlanRequest {
  planId: PlanId;
  paymentType: AdminPaymentType;
  paymentMethod?: ManualPaymentMethod;
  basePlanId?: PlanId;
  customOverrides?: {
    display_name?: string;
    amount?: number;
    interval?: 'month' | 'year';
    quotas?: Partial<PlanQuotas>;
    channels_available?: string[];
    features?: Partial<PlanFeatures>;
  };
  notes?: string;
  durationDays?: number;
  adminId?: string;
}

export const ALL_PLAN_CHANNELS = [
  'web_widget',
  'messenger',
  'instagram',
  'whatsapp',
  'callbot',
  'alexa',
] as const;

export const CHANNEL_LABELS: Record<string, string> = {
  web_widget: 'Web Widget',
  messenger: 'Messenger',
  facebook: 'Messenger',
  instagram: 'Instagram Direct',
  whatsapp: 'WhatsApp',
  callbot: 'Callbot',
  alexa: 'Alexa',
};

export function resolvePlanPosition(planId: PlanId | string, explicit?: number): number {
  if (explicit != null && Number.isFinite(explicit)) return explicit;
  return DEFAULT_PLAN_POSITIONS[planId as PlanId] ?? 99;
}

export function sortPlansByPosition<T extends { plan_id: PlanId | string; position?: number; amount?: number }>(
  plans: T[],
): T[] {
  return [...plans].sort((a, b) => {
    const posA = resolvePlanPosition(a.plan_id, a.position);
    const posB = resolvePlanPosition(b.plan_id, b.position);
    if (posA !== posB) return posA - posB;
    return (a.amount ?? 0) - (b.amount ?? 0);
  });
}

export const PLAN_UPGRADE_LABELS: Record<PlanId, string> = {
  free: 'Starter',
  starter: 'Pro',
  pro: 'Custom',
  custom: 'Custom',
};
